/**
 * "Named on" — requests someone else raised that name the reader (2 Oct).
 *
 * HR raises a New User Request and names the starter's manager in its
 * `ReportingTo` field; the manager then finds it under My tickets › Named on
 * and may follow it and comment. The server says which person field named
 * them (`named_as`, an Ivanti internal name); this turns it into a translated
 * role. Pure, so `tests/named-on.test.mjs` can run it without React.
 */

/** The person fields with a label of their own, as the server names them. */
export const NAMED_ON_ROLES = [
  "ReportingTo",
  "RequestedFor",
  "NewUser",
  "MailboxDelegate",
  "OneDriveDelegate",
  "NewManagerForReports",
  "AffectedUser",
  "User",
  // 14 Oct: a member of an access request's owner team (no field names them).
  "OwnerTeam",
] as const;

export type NamedOnRole = (typeof NAMED_ON_ROLES)[number];

export type NamedOnRoleKey = `namedOn.role.${NamedOnRole}`;

/** The two tabs of My tickets, as they appear in `?view=`. */
export type TicketListView = "mine" | "named-on";

/** Translation key for a known person field, or null for one we have no label for. */
export function namedOnRoleKey(parameterName: string | null | undefined): NamedOnRoleKey | null {
  return (NAMED_ON_ROLES as readonly string[]).includes(parameterName ?? "")
    ? (`namedOn.role.${parameterName}` as NamedOnRoleKey)
    : null;
}

/**
 * A readable stand-in for a person field the UI has no label for.
 *
 * The allowlist is configuration, so a field can be added on the server before
 * it is given a translation here: `LineManager_New` reads "Line manager new"
 * rather than as an identifier.
 */
export function humaniseParameterName(parameterName: string): string {
  const words = parameterName
    .replace(/[_-]+/g, " ")
    .replace(/(?<=[a-z0-9])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) {
    return parameterName;
  }
  // First word capitalised, acronyms kept ("CRM"), everything else lower case.
  return words
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1)
        : /^[A-Z0-9]{2,}$/.test(word)
          ? word
          : word.toLowerCase(),
    )
    .join(" ");
}

/** The tab a `?view=` value selects; anything unrecognised is the reader's own. */
export function ticketListView(param: string | null | undefined): TicketListView {
  return param === "named-on" ? "named-on" : "mine";
}

/** True when the reader is following someone else's request, not raising their own. */
export function isNamedView(ticket: { named_as?: string | null }): boolean {
  return typeof ticket.named_as === "string" && ticket.named_as.length > 0;
}
